import React, { useState, useRef, useEffect } from "react";
import { Mic, Square, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { getStoredUser } from "./RegisterModal";

/**
 * Robust mobile-friendly mic button.
 * - Picks the best supported mime type per platform (Android/iOS/desktop)
 * - Surfaces permission errors visibly (toast + state) instead of failing silently
 * - Tap to start, tap again to stop. Auto-stop after 60s safety cap.
 */
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
  "audio/aac",
];

function pickMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {/* noop */}
  }
  return null;
}

function extFor(mime) {
  if (!mime) return "webm";
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("aac")) return "aac";
  return "webm";
}

function showToast(msg) {
  // Lightweight toast using DOM (avoids dragging in a state lib)
  const el = document.createElement("div");
  el.textContent = msg;
  el.style.cssText = (
    "position:fixed;left:50%;bottom:88px;transform:translateX(-50%);" +
    "background:rgba(0,0,0,0.92);color:#F5F5F5;border:1px solid rgba(233,185,52,0.4);" +
    "padding:10px 16px;border-radius:8px;font:500 13px -apple-system,sans-serif;" +
    "z-index:99999;max-width:90%;text-align:center;box-shadow:0 8px 24px rgba(0,0,0,0.5);"
  );
  document.body.appendChild(el);
  setTimeout(() => { el.style.opacity = "0"; el.style.transition = "opacity 300ms"; }, 2700);
  setTimeout(() => el.remove(), 3100);
}

export default function VoiceInput({ onText, disabled, className = "" }) {
  const { lang, t } = useI18n();
  const [state, setState] = useState("idle"); // idle | recording | transcribing | error
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const safetyTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      stopStream();
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tk) => tk.stop());
      streamRef.current = null;
    }
  };

  const start = async () => {
    if (state !== "idle") return;

    // Pre-flight checks with clear user feedback
    if (typeof window === "undefined" || !window.isSecureContext) {
      showToast(lang === "pt" ? "Microfone exige conexão segura (HTTPS)." : "Mic requires a secure (HTTPS) connection.");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      showToast(lang === "pt" ? "Seu navegador não suporta gravação. Tente Chrome ou Safari." : "Browser doesn't support recording. Try Chrome or Safari.");
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      showToast(lang === "pt" ? "Seu navegador não suporta gravação de áudio." : "Browser doesn't support audio recording.");
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        showToast(lang === "pt"
          ? "Permita o microfone nas configurações do navegador e tente de novo."
          : "Allow microphone in browser settings and try again.");
      } else if (name === "NotFoundError") {
        showToast(lang === "pt" ? "Nenhum microfone encontrado." : "No microphone found.");
      } else {
        showToast(lang === "pt" ? "Não consegui acessar o microfone." : "Couldn't access microphone.");
      }
      setState("idle");
      return;
    }

    streamRef.current = stream;
    const mime = pickMime();
    let rec;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      stopStream();
      showToast(lang === "pt" ? "Formato de áudio não suportado neste celular." : "Audio format unsupported on this device.");
      setState("idle");
      return;
    }
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onerror = () => {
      showToast(lang === "pt" ? "Erro na gravação. Tente novamente." : "Recording error. Try again.");
      stopStream();
      setState("idle");
    };
    rec.onstop = async () => {
      if (safetyTimerRef.current) { clearTimeout(safetyTimerRef.current); safetyTimerRef.current = null; }
      stopStream();
      const ext = extFor(rec.mimeType || mime);
      const blobType = (rec.mimeType || mime || "audio/webm").split(";")[0];
      const blob = new Blob(chunksRef.current, { type: blobType });
      if (blob.size < 1000) {
        showToast(lang === "pt" ? "Áudio muito curto. Segura mais tempo." : "Audio too short. Hold longer.");
        setState("idle");
        return;
      }
      setState("transcribing");
      try {
        const form = new FormData();
        form.append("audio", blob, `speech.${ext}`);
        form.append("lang", lang);
        const email = getStoredUser()?.email;
        if (email) form.append("email", email);
        const res = await api.post("/ai/transcribe", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        const text = (res.data?.text || "").trim();
        if (text && onText) {
          onText(text);
        } else {
          showToast(lang === "pt" ? "Não entendi o áudio. Tente de novo mais alto." : "Couldn't understand audio. Try again louder.");
        }
      } catch (err) {
        console.error("transcribe", err);
        if (err?.response?.status === 402 && err?.response?.data?.detail) {
          const d = err.response.data.detail;
          window.dispatchEvent(new CustomEvent("goldora:quota-blocked", { detail: { ...d, feature: "transcribe" } }));
        } else {
          showToast(lang === "pt" ? "Falha ao transcrever. Tente novamente." : "Transcription failed. Try again.");
        }
      } finally {
        setState("idle");
      }
    };

    try {
      rec.start();
    } catch {
      stopStream();
      showToast(lang === "pt" ? "Não consegui iniciar a gravação." : "Couldn't start recording.");
      setState("idle");
      return;
    }

    mediaRecorderRef.current = rec;
    setState("recording");
    // Safety auto-stop after 60s
    safetyTimerRef.current = setTimeout(() => {
      try { rec.state === "recording" && rec.stop(); } catch {/* noop */}
    }, 60000);
  };

  const stop = () => {
    const rec = mediaRecorderRef.current;
    if (rec && state === "recording") {
      try { rec.state === "recording" && rec.stop(); } catch {/* noop */}
    }
  };

  const toggle = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (state === "recording") stop();
    else if (state === "idle") start();
  };

  const label =
    state === "recording" ? t.ai.recording : state === "transcribing" ? t.ai.transcribing : t.ai.speak;

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || state === "transcribing"}
      title={label}
      aria-label={label}
      data-testid="voice-input-btn"
      className={`relative inline-flex items-center justify-center w-10 h-10 rounded-md border transition-all ${
        state === "recording"
          ? "bg-red-500/15 border-red-500/50 text-red-400 animate-pulse"
          : state === "transcribing"
          ? "bg-white/5 border-white/10 text-neutral-400"
          : state === "error"
          ? "bg-red-500/10 border-red-500/30 text-red-300"
          : "bg-white/5 border-white/10 text-neutral-300 hover:border-gold-500/50 hover:text-gold-400 active:bg-gold-500/10"
      } disabled:opacity-40 ${className}`}
      style={{ touchAction: "manipulation" }}
    >
      {state === "transcribing" ? (
        <Loader2 size={16} className="animate-spin" />
      ) : state === "recording" ? (
        <Square size={14} />
      ) : state === "error" ? (
        <AlertCircle size={16} />
      ) : (
        <Mic size={16} />
      )}
      {state === "recording" && (
        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 animate-ping" />
      )}
    </button>
  );
}
